import { StatusChip } from './StatusChip';

// **D49 — ΕΝΑ πεδίο προτάσεων, για όλα τα πεδία που έχουν προτάσεις.**
//
// Το σχέδιο υπήρχε ήδη και δούλευε: η οθόνη προφίλ πρόσφερε τα ενδιαφέροντα «που
// χρησιμοποιούνται ήδη στο σύστημα» ως πατήσιμα chips. Ήταν όμως γραμμένο **μέσα**
// στην οθόνη, οπότε δεν είχε όνομα — και *σχέδιο χωρίς όνομα δεν
// επαναχρησιμοποιείται* (ο ίδιος λόγος που η φυσαλίδα του σχολίου απέκτησε το δικό
// της στη **D41**). Το αποτέλεσμα ήταν ότι οι **ετικέτες**, όπου η σύγκλιση της
// ορολογίας μετράει περισσότερο απ' ό,τι στα ενδιαφέροντα, δεν πρόσφεραν καμία
// πρόταση: κάθε μέλος της ομάδας τις έγραφε από την αρχή, και το «χρηματοδότηση»,
// το «Χρηματοδότηση» και το «χρηματοδοτηση» γίνονταν τρεις διαφορετικές ετικέτες
// που κανένα φίλτρο δεν ενώνει.
//
// ── ΤΙ ΕΙΝΑΙ, ΚΑΙ ΤΙ ΔΕΝ ΕΙΝΑΙ ───────────────────────────────────────────────
//
// Είναι **η περιοχή των προτάσεων**, όχι το πεδίο εισαγωγής. Η διάκριση είναι
// σκόπιμη: τα τρία σημεία που το χρησιμοποιούν έχουν **διαφορετικό** πεδίο —
// κείμενο με κόμματα στο ένα, πεδίο ετικετών στο άλλο — και ενοποίησή τους θα
// σήμαινε ξαναγράψιμο τριών φορμών για να κερδηθεί μία λίστα chips.
//
// **Καμία κλήση δικτύου εδώ.** Τις προτάσεις τις δίνει ο καλών, γιατί μόνο εκείνος
// ξέρει από πού έρχονται: του συστήματος για τα ενδιαφέροντα, **της συλλογής** για
// τις ετικέτες. Πεδίο που φέρνει μόνο του τα δεδομένα του θα έδενε την εμφάνιση με
// μία συγκεκριμένη διαδρομή.

/**
 * @param {string[]} suggestions Ο κατάλογος των προτάσεων. Κενός → δεν αποδίδεται τίποτα.
 * @param {string[]} value       Ό,τι έχει ήδη επιλεγεί· αφαιρείται από τις προτάσεις.
 * @param {(value: string) => void} onAdd Τι σημαίνει «διάλεξα αυτό». Ο καλών ξέρει
 *   πού γράφεται η τιμή — εδώ δεν υπάρχει κατάσταση.
 * @param {boolean} multiple Αν επιτρέπονται **πολλαπλές** τιμές. Με `false` η περιοχή
 *   εξαφανίζεται μόλις υπάρχει τιμή: δεύτερη πρόταση εκεί όπου χωράει μία είναι
 *   κουμπί που αντικαθιστά σιωπηλά ό,τι διάλεξε ο χρήστης.
 * @param {boolean} allowFree Αν το πεδίο δέχεται και **ελεύθερη** τιμή.
 *
 *   ⚠ **Αλλάζει ΜΟΝΟ το κείμενο της υπόδειξης, και δηλώνεται.** Η αποδοχή ή η
 *   άρνηση ελεύθερης τιμής επιβάλλεται από το **πεδίο** και από το σχήμα του API,
 *   ποτέ από εδώ. Η περιοχή προτάσεων το γνωρίζει για έναν λόγο: για να λέει την
 *   αλήθεια. «Πατήστε για προσθήκη» δίπλα σε πεδίο που δέχεται μόνο τις προτάσεις
 *   υπονοεί ότι μπορείτε και να γράψετε δική σας — και το αντίθετο κρύβει ότι
 *   μπορείτε.
 */
export function SuggestionsField({
    suggestions = [],
    value = [],
    onAdd,
    multiple = true,
    allowFree = true,
    label = 'Προτάσεις',
    hint
}) {
    // Με μία μόνο επιτρεπτή τιμή, η ύπαρξη τιμής τερματίζει τις προτάσεις.
    if (!multiple && value.length > 0) return null;

    const chosen = new Set(value.map((item) => item.toLocaleLowerCase('el')));
    // **Πεζοποίηση στη σύγκριση και μόνο.** Η πρόταση αποδίδεται με την ορθογραφία
    // που έχει στο σύστημα — αυτό είναι ολόκληρο το νόημά της. Αν κάποιος έγραψε
    // ήδη «Έρευνα» δεν του ξαναπροτείνεται το «έρευνα» ως δεύτερη ετικέτα, γιατί
    // θα ήταν ακριβώς το διπλότυπο που η περιοχή υπάρχει για να αποτρέψει.
    //
    // ⚠ **ΔΗΛΩΜΕΝΟ ΟΡΙΟ: καμία εξομάλυνση τονισμού.** Το «ερευνα» χωρίς τόνο
    // εξακολουθεί να προσφέρεται δίπλα στο «έρευνα». Ο κανόνας εξομάλυνσης
    // υπάρχει ήδη **στον διακομιστή**, για την αναζήτηση, και τρίτη διατύπωσή του
    // στον πελάτη είναι ακριβώς η υπογραφή που έχει κοστίσει πέντε φορές — ίδιο
    // σκεπτικό με το δηλωμένο όριο του επιλογέα αναφορών (**D44**).
    const offered = suggestions.filter((suggestion) => !chosen.has(suggestion.toLocaleLowerCase('el')));

    if (offered.length === 0) return null;

    const defaultHint = allowFree
        ? 'Χρησιμοποιούνται ήδη. Πατήστε για προσθήκη, ή γράψτε δική σας:'
        : 'Διαλέξτε από όσες χρησιμοποιούνται ήδη:';

    return (
        <div className="flex flex-col gap-2">
            <p className="text-sm text-muted" id="suggestions-hint">{hint ?? defaultHint}</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
                {offered.map((suggestion) => (
                    <StatusChip
                        key={suggestion}
                        as="button"
                        type="button"
                        icon="fa-solid fa-plus"
                        tone="purple"
                        appearance="outline"
                        onClick={() => onAdd(suggestion)}
                        className="cursor-pointer hover:bg-accent-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                    >
                        {suggestion}
                    </StatusChip>
                ))}
            </div>
        </div>
    );
}

export default SuggestionsField;
